import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { assign, debounce, omit } from 'lodash';

// forked from: https://github.com/eiriklv/react-masonry-component/blob/master/lib/index.js

type MasonryCtor = typeof import('masonry-layout').default;
type MasonryInstance = InstanceType<MasonryCtor>;
type ImagesLoaded = typeof import('imagesloaded').default;

let Masonry: MasonryCtor | null;
let imagesloaded: ImagesLoaded | null;

const MASONRY_OMIT_PROPS = [
  'disableImagesLoaded',
  'onImagesLoaded',
  'updateOnEachImageLoad',
  'options',
  'imagesLoadedOptions',
  'elementType',
  'onLayoutComplete',
  'onRemoveComplete',
  'children',
];

type Props = {
  children?: React.ReactNode;
  elementType?: React.ElementType;
  options?: Record<string, unknown>;
  imagesLoadedOptions?: Record<string, unknown>;
  disableImagesLoaded?: boolean;
  updateOnEachImageLoad?: boolean;
  onImagesLoaded?: (instance: unknown) => void;
  onLayoutComplete?: (...args: unknown[]) => void;
  onRemoveComplete?: (...args: unknown[]) => void;
} & React.HTMLAttributes<HTMLElement>;

// componentDidMount/DidUpdate timing (before paint); useLayoutEffect warns during SSR
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const MasonryComponent: React.FC<Props> = (props) => {
  const masonryContainer = useRef<HTMLElement>(null);
  const masonry = useRef<MasonryInstance>();
  const latestKnownDomChildren = useRef<Element[]>([]);
  const imagesLoadedCancelRef = useRef<() => void>();
  const latestProps = useRef(props);
  latestProps.current = props;

  if (typeof Masonry === 'undefined') {
    const isBrowser = typeof window !== 'undefined';
    Masonry = isBrowser ? window.Masonry || require('masonry-layout') : null;
    imagesloaded = isBrowser ? require('imagesloaded') : null;
  }

  const initializeMasonry = () => {
    masonry.current = new Masonry!(masonryContainer.current!, props.options);

    if (props.onLayoutComplete) {
      masonry.current.on('layoutComplete', props.onLayoutComplete);
    }

    if (props.onRemoveComplete) {
      masonry.current.on('removeComplete', props.onRemoveComplete);
    }

    latestKnownDomChildren.current = getCurrentDomChildren();
  };

  const getCurrentDomChildren = (): Element[] => {
    const node = masonryContainer.current!;
    const itemSelector = props.options?.itemSelector as string | undefined;
    const children = itemSelector ? node.querySelectorAll(itemSelector) : node.children;
    return Array.prototype.slice.call(children);
  };

  const diffDomChildren = () => {
    let forceItemReload = false;

    const knownChildrenStillAttached = latestKnownDomChildren.current.filter(
      ({ parentNode }) =>
        /*
         * take only elements attached to DOM
         * (aka the parent is the masonry container, not null)
         * otherwise masonry would try to "remove it" again from the DOM
         */ !!parentNode,
    );

    /*
     * If not all known children are attached to the dom - we have no other way of notifying
     * masonry to remove the ones not still attached besides invoking a complete item reload.
     * basically all the rest of the code below does not matter in that case.
     */
    if (knownChildrenStillAttached.length !== latestKnownDomChildren.current.length) {
      forceItemReload = true;
    }

    const currentDomChildren = getCurrentDomChildren();

    /*
     * Since we are looking for a known child which is also attached to the dom AND
     * not attached to the dom at the same time - this would *always* produce an empty array.
     */
    const removed = knownChildrenStillAttached.filter(
      (attachedKnownChild) => !~currentDomChildren.indexOf(attachedKnownChild),
    );

    /*
     * This would get any children which are attached to the dom but are *unkown* to us
     * from previous renders
     */
    const newDomChildren = currentDomChildren.filter(
      (currentChild) => !~knownChildrenStillAttached.indexOf(currentChild),
    );

    let beginningIndex = 0;

    // get everything added to the beginning of the DOMNode list
    const prepended = newDomChildren.filter((newChild) => {
      const prepend = beginningIndex === currentDomChildren.indexOf(newChild);

      if (prepend) {
        // increase the index
        beginningIndex++;
      }

      return prepend;
    });

    // we assume that everything else is appended
    const appended = newDomChildren.filter((el) => !prepended.includes(el));

    /*
     * otherwise we reverse it because so we're going through the list picking off the items that
     * have been added at the end of the list. this complex logic is preserved in case it needs to be
     * invoked
     *
     * var endingIndex = currentDomChildren.length - 1;
     *
     * newDomChildren.reverse().filter(function(newChild, i){
     *     var append = endingIndex == currentDomChildren.indexOf(newChild);
     *
     *     if (append) {
     *         endingIndex--;
     *     }
     *
     *     return append;
     * });
     */

    // get everything added to the end of the DOMNode list
    let moved: Element[] = [];

    /*
     * This would always be true (see above about the lofic for "removed")
     */
    if (removed.length === 0) {
      /*
       * 'moved' will contain some random elements (if any) since the "knownChildrenStillAttached" is a filter
       * of the "known" children which are still attached - All indexes could basically change. (for example
       * if the first element is not attached)
       * Don't trust this array.
       */
      moved = knownChildrenStillAttached.filter(
        (child, index) => index !== currentDomChildren.indexOf(child),
      );
    }

    latestKnownDomChildren.current = currentDomChildren;

    return {
      old: knownChildrenStillAttached, // Not used
      new: currentDomChildren, // Not used
      removed,
      appended,
      prepended,
      moved,
      forceItemReload,
    };
  };

  const performLayout = (instance: MasonryInstance) => {
    const diff = diffDomChildren();
    let reloadItems = diff.forceItemReload || diff.moved.length > 0;

    // Would never be true. (see comments of 'diffDomChildren' about 'removed')
    if (diff.removed.length > 0) {
      instance.remove(diff.removed);
      reloadItems = true;
    }

    if (diff.appended.length > 0) {
      instance.appended(diff.appended);

      if (diff.prepended.length === 0) {
        reloadItems = true;
      }
    }

    if (diff.prepended.length > 0) {
      instance.prepended(diff.prepended);
    }

    if (reloadItems) {
      instance.reloadItems();
    }

    instance.layout();
  };

  const derefImagesLoaded = () => {
    imagesLoadedCancelRef.current?.();
    imagesLoadedCancelRef.current = undefined;
  };

  const imagesLoaded = () => {
    const {
      disableImagesLoaded = false,
      updateOnEachImageLoad = false,
      imagesLoadedOptions = {},
      onImagesLoaded,
    } = props;

    if (disableImagesLoaded) {
      return;
    }

    if (imagesLoadedCancelRef.current) {
      derefImagesLoaded();
    }

    const event = updateOnEachImageLoad ? 'progress' : 'always';
    const handler = debounce((instance: unknown) => {
      if (onImagesLoaded) {
        onImagesLoaded(instance);
      }
      masonry.current!.layout();
    }, 100);

    const imgLoad = imagesloaded!(masonryContainer.current!, imagesLoadedOptions).on(
      event,
      handler,
    );

    imagesLoadedCancelRef.current = () => {
      imgLoad.off(event, handler);
      handler.cancel();
    };
  };

  useIsomorphicLayoutEffect(() => {
    if (masonry.current) {
      performLayout(masonry.current);
    } else {
      initializeMasonry();
    }
    imagesLoaded();
  });

  useIsomorphicLayoutEffect(
    () => () => {
      const { onLayoutComplete, onRemoveComplete } = latestProps.current;

      // unregister events
      if (onLayoutComplete) {
        masonry.current!.off('layoutComplete', onLayoutComplete);
      }

      if (onRemoveComplete) {
        masonry.current!.off('removeComplete', onRemoveComplete);
      }

      if (imagesLoadedCancelRef.current) {
        derefImagesLoaded();
      }
      masonry.current!.destroy();
      masonry.current = undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return React.createElement(
    props.elementType || 'div',
    assign({}, omit(props, MASONRY_OMIT_PROPS), { ref: masonryContainer }),
    props.children,
  );
};

export default MasonryComponent;
